import React, { useState, useEffect } from 'react';
import CookingPotAnimation from './CookingPotAnimation';
import IngredientIcon from './IngredientIcon';
import { useDaggerheartData } from '../../context/DataContext';

/**
 * BeastFeastAnimationDemo Component
 * A demo/testing component to showcase the cooking animation system
 * with real Beast Feast ingredient data
 */
const BeastFeastAnimationDemo = () => {
  const allData = useDaggerheartData();
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [isStirring, setIsStirring] = useState(false);
  const [cookingPhase, setCookingPhase] = useState('preparation');
  const [bubbleIntensity, setBubbleIntensity] = useState('medium');

  // Get real ingredient data from Beast Feast campaign frame
  const beastFeastData = allData?.campaignFrames?.['beast-feast'];
  const exampleIngredients = beastFeastData?.customMechanics?.exampleIngredients || [];
  const specialIngredients = beastFeastData?.customMechanics?.specialIngredients || [];
  
  // Generate some additional beast parts and blooms for demo
  const beastParts = beastFeastData?.customMechanics?.ingredientTables?.beastParts || [];
  const bloomTypes = beastFeastData?.customMechanics?.ingredientTables?.bloomTypes || [];
  const uniqueTraits = beastFeastData?.customMechanics?.ingredientTables?.uniqueTraits || [];
  
  // Create comprehensive ingredient list from actual data
  const generateDemoIngredients = () => {
    const demoIngredients = [...exampleIngredients, ...specialIngredients];
    
    // Add some beast parts with traits
    beastParts.slice(0, 8).forEach((part, index) => {
      const trait = uniqueTraits[index % uniqueTraits.length];
      const flavorProfiles = ['Sweet', 'Salty', 'Bitter', 'Sour', 'Savory', 'Weird'];
      const randomFlavor = flavorProfiles[Math.floor(Math.random() * flavorProfiles.length)];
      
      demoIngredients.push({
        name: trait ? `Beast ${part} (${trait})` : `Beast ${part}`,
        flavors: [{ profile: randomFlavor, strength: Math.floor(Math.random() * 2) + 1 }]
      });
    });
    
    // Add some bloom types with traits
    bloomTypes.slice(0, 6).forEach((bloom, index) => {
      const trait = uniqueTraits[(index + 8) % uniqueTraits.length];
      const flavorProfiles = ['Sweet', 'Salty', 'Bitter', 'Sour', 'Savory', 'Weird'];
      const randomFlavor = flavorProfiles[Math.floor(Math.random() * flavorProfiles.length)];
      
      demoIngredients.push({
        name: trait ? `${bloom} (${trait})` : bloom,
        flavors: [{ profile: randomFlavor, strength: Math.floor(Math.random() * 2) + 1 }]
      });
    });
    
    return demoIngredients;
  };
  
  const availableIngredients = generateDemoIngredients();

  const handleAddIngredient = (ingredient) => {
    if (selectedIngredients.length < 6) { // Limit for demo
      setSelectedIngredients(prev => [...prev, ingredient]);
    }
  };

  const handleStirClick = () => {
    if (selectedIngredients.length > 0) {
      setIsStirring(!isStirring);
      if (!isStirring) {
        setCookingPhase('cooking');
      }
    }
  };

  const handleStartCooking = () => {
    if (selectedIngredients.length > 0) {
      setCookingPhase('cooking');
      setBubbleIntensity('high');
      
      // Simulate cooking process
      setTimeout(() => {
        setCookingPhase('finished');
        setIsStirring(false);
        setBubbleIntensity('low');
      }, 8000);
    }
  };

  const handleReset = () => {
    setSelectedIngredients([]);
    setIsStirring(false);
    setCookingPhase('preparation');
    setBubbleIntensity('medium');
    
    // Force pot to reset by clearing dropped ingredients and resetting liquid color
    // We'll pass a reset trigger to the CookingPotAnimation component
    setResetTrigger(prev => prev + 1);
  };

  const [resetTrigger, setResetTrigger] = useState(0);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-b from-amber-50 to-orange-100 min-h-screen">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-amber-900 mb-2">
          🍖 Beast Feast Cooking Animation Demo
        </h1>
        <p className="text-amber-700">
          Experience the poffin-style cooking mechanics with visual ingredient system!
        </p>
      </div>

      {/* Cooking Animation Area */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Cooking Pot</h2>
          <div className="flex justify-center space-x-4 text-sm text-gray-600">
            <span className={`px-2 py-1 rounded ${cookingPhase === 'preparation' ? 'bg-blue-200' : 'bg-gray-200'}`}>
              Preparation
            </span>
            <span className={`px-2 py-1 rounded ${cookingPhase === 'cooking' ? 'bg-orange-200' : 'bg-gray-200'}`}>
              Cooking
            </span>
            <span className={`px-2 py-1 rounded ${cookingPhase === 'finished' ? 'bg-green-200' : 'bg-gray-200'}`}>
              Finished
            </span>
          </div>
        </div>

                <CookingPotAnimation
          ingredients={selectedIngredients}
          isStirring={isStirring}
          stirSpeed={80}
          bubbleIntensity={bubbleIntensity}
          onStirClick={handleStirClick}
          cookingPhase={cookingPhase}
          resetTrigger={resetTrigger}
        />

        <div className="text-center mt-6 space-x-4">
          <button
            onClick={handleStartCooking}
            disabled={selectedIngredients.length === 0 || cookingPhase === 'cooking'}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Cooking
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Reset Pot
          </button>
        </div>
      </div>

      {/* Selected Ingredients Display */}
      {selectedIngredients.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Ingredients in Pot ({selectedIngredients.length}/6)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {selectedIngredients.map((ingredient, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                <IngredientIcon 
                  ingredientName={ingredient.name} 
                  size={24}
                  animate={isStirring}
                />
                <span className="text-sm text-gray-700">{ingredient.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Ingredients */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Ingredients</h3>
        <p className="text-sm text-gray-600 mb-4">
          Click ingredients to add them to the pot. Watch them drop in with custom animations!
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {availableIngredients.map((ingredient, index) => (
            <button
              key={index}
              onClick={() => handleAddIngredient(ingredient)}
              disabled={selectedIngredients.length >= 6}
              className="flex items-center space-x-3 p-3 border border-gray-200 rounded hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <IngredientIcon 
                ingredientName={ingredient.name} 
                size={20}
                glowing={true}
              />
              <div className="text-left">
                <div className="text-sm font-medium text-gray-800">
                  {ingredient.name}
                </div>
                {ingredient.flavors && (
                  <div className="text-xs text-gray-500">
                    {ingredient.flavors.map(f => `${f.profile}(${f.strength})`).join(', ')}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Icon Showcase */}
      <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Ingredient Icon Showcase</h3>
        <p className="text-sm text-gray-600 mb-4">
          All ingredient types with their visual representations:
        </p>
        
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-4">
          {[
            "Beast Meat", "Beast Flesh", "Beast Eggs", "Beast Tongue", "Beast Brain", "Beast Ribs",
            "Beast Marrow", "Beast Eyes", "Beast Wings", "Beast Scales", "Beast Secretion", "Beast Fat",
            "Berries", "Fruit", "Leaves", "Flower", "Nuts", "Seeds", "Roots", "Bark",
            "Mushroom", "Fungi", "Herbs", "Sap", "Nectar", "Pollen"
          ].map((name) => (
            <div key={name} className="text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-lg mb-2">
                <IngredientIcon ingredientName={name} size={24} />
              </div>
              <div className="text-xs text-gray-600 leading-tight">{name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BeastFeastAnimationDemo;
